import { useEffect, useState } from "react"

import { useInterval } from "../../../shared/hooks/useInterval"

import { MENU_DISPLAY_SLIDE_INTERVAL_MS, MENU_DISPLAY_SLIDE_TRANSITION_MS } from "./menuDisplayData"

type MenuCarousel = Readonly<{
    offsetIndex: number
    activeIndex: number
    animate: boolean
}>

/**
 * Auto-advancing carousel state machine for the menu display. Advances one dish every
 * MENU_DISPLAY_SLIDE_INTERVAL_MS and loops seamlessly via a cloned first slide.
 */
export function useMenuCarousel(count: number): MenuCarousel {
    const [offsetIndex, setOffsetIndex] = useState(0)
    const [animate, setAnimate] = useState(true)

    // Restart the loop when the menu itself changes (e.g. live data replacing the loading state).
    useEffect(() => {
        setAnimate(false)
        setOffsetIndex(0)
    }, [count])

    useInterval(() => setOffsetIndex((index) => index + 1), MENU_DISPLAY_SLIDE_INTERVAL_MS)

    useEffect(() => {
        if (offsetIndex === count) {
            // Reached the clone: let the slide finish, then jump back to the real first slide with no
            // transition (visually identical, so the reset is invisible).
            const timeoutId = setTimeout(() => {
                setAnimate(false)
                setOffsetIndex(0)
            }, MENU_DISPLAY_SLIDE_TRANSITION_MS)
            return () => clearTimeout(timeoutId)
        }
        if (!animate) {
            // Re-enable the transition on the next frame, after the snap-back has painted.
            const frameId = requestAnimationFrame(() => setAnimate(true))
            return () => cancelAnimationFrame(frameId)
        }
        return undefined
    }, [offsetIndex, animate, count])

    return { offsetIndex, activeIndex: count > 0 ? offsetIndex % count : 0, animate }
}
